// offlineQueue.js — Offline-Puffer für Scoring-Requests
// Speichert Wertungen und Kampfergebnisse, die beim Netzwerkausfall nicht
// durchkamen, in IndexedDB und sendet sie nach Reconnect automatisch nach.
//
// ⚠️ NUR die Positivliste unten (18.10.2026). Alles andere (Bracket erzeugen,
// Check-in, Siegerehrung ...) lehnt index.js bei Netzausfall ab: das später
// nachzusenden ist gefährlich (ein Check-in-Umschalter schaltete zurück,
// Neu-Generieren löschte nachträglich Ergebnisse).
const PUFFERBAR = [
  { method: 'PUT',  re: /^\/api\/brackets\/kampf\/\d+\/result(\?|$)/ },  // Kampfergebnis
  { method: 'POST', re: /^\/api\/formen\/wertung(\?|$)/ },                  // Formen/SV-Noten
  { method: 'POST', re: /^\/api\/bruchtest\/wertung(\?|$)/ },               // Bruchtest
];

export function darfPuffern(url, method) {
  const pfad = String(url || '');
  const m = String(method || '').toUpperCase();
  return PUFFERBAR.some((p) => p.method === m && p.re.test(pfad));
}

const DB_NAME    = 'tda-offline-queue';
const DB_VERSION = 1;
const STORE      = 'pending';

// Flag: verhindert dass Replay-Requests erneut in die Queue landen
let _replaying = false;
export const flags = {
  get isReplaying() { return _replaying; }
};

// ── DB-Zugriff ────────────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

export async function enqueue({ url, method, body, label, auth }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add({ url, method, body, label, auth: auth || null, ts: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function getAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function remove(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = resolve;
    tx.onerror    = reject;
  });
}

export async function getPendingCount() {
  const all = await getAll();
  return all.length;
}

// ── Labels ────────────────────────────────────────────────────────────────────

export function labelFromUrl(url, method) {
  if (url.includes('/bruchtest/wertung'))  return 'Bruchtest-Wertung';
  if (url.includes('/formen/wertung'))     return 'Formen-Wertung';
  if (url.includes('/einwaage'))           return 'Einwaage';
  if (url.includes('/kampf') || url.includes('/rumble')) return 'Kampf-Wertung';
  if (url.includes('/wertung'))            return 'Wertung';
  if (method === 'DELETE')                 return 'Löschen';
  return 'Anfrage';
}

// ── Replay ────────────────────────────────────────────────────────────────────

// Antwort wird geprüft (18.10.2026):
//   2xx           -> gesendet, Eintrag weg
//   4xx           -> der Server lehnt ab (Recht, schon gewertet, Kampf weg ...):
//                    Nachsenden hilft nie, Eintrag weg und als "verworfen" melden
//   5xx / Netz    -> bleibt liegen, nächster Versuch bei der nächsten Verbindung
// Läuft schon ein Nachsenden, startet kein zweites (sonst doppelt gesendet).
let _laufendesNachsenden = null;

export async function replayAll() {
  if (_laufendesNachsenden) return _laufendesNachsenden;
  _laufendesNachsenden = nachsenden().finally(() => { _laufendesNachsenden = null; });
  return _laufendesNachsenden;
}

async function nachsenden() {
  const entries = await getAll();
  if (entries.length === 0) return { replayed: 0, failed: 0, verworfen: [] };

  _replaying = true;
  window.dispatchEvent(new CustomEvent('offline-queue-syncing', { detail: { total: entries.length } }));

  let replayed = 0;
  let failed   = 0;
  const verworfen = [];

  try {
    for (const entry of entries) {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (entry.auth) headers.Authorization = entry.auth;
        const resp = await window.fetch(entry.url, {
          method:  entry.method,
          headers,
          body:    entry.body,
        });

        if (resp.ok) {
          await remove(entry.id);
          replayed++;
          window.dispatchEvent(new Event('offline-queue-changed'));
        } else if (resp.status >= 400 && resp.status < 500) {
          let grund = `HTTP ${resp.status}`;
          try {
            const d = await resp.json();
            grund = d.message || d.error || grund;
          } catch { /* kein JSON */ }
          await remove(entry.id);
          verworfen.push({ label: entry.label, status: resp.status, grund, ts: entry.ts });
          window.dispatchEvent(new Event('offline-queue-changed'));
          console.warn(`[OfflineQueue] verworfen (${resp.status}) ${entry.url}: ${grund}`);
        } else {
          failed++;
          console.warn(`[OfflineQueue] HTTP ${resp.status} beim Replay von ${entry.url}, bleibt liegen`);
        }
      } catch (e) {
        failed++;
        console.warn(`[OfflineQueue] Netzwerkfehler beim Replay von ${entry.url}:`, e.message);
      }
    }
  } finally {
    _replaying = false;
  }
  window.dispatchEvent(new CustomEvent('offline-queue-synced', { detail: { replayed, failed, verworfen } }));
  return { replayed, failed, verworfen };
}
